import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  linkedSignal,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute } from '@angular/router';
import { AssetDto, AssetImageTypeDataDto, GalleryDto, GalleryTheme } from '@nusign/picflow-backend-types';
import { map } from 'rxjs';
import { Rect } from '../../../classes/rect.class';
import { AudioViewerComponent } from '../../../gallery-shared/components/audio-viewer/audio-viewer.component';
import { CommentThread } from '../../../gallery-shared/components/comments/comments.component';
import {
  VideoTimeData,
  VideoViewerComponent,
} from '../../../gallery-shared/components/video-viewer/video-viewer.component';
import { CanDisplayAssetPipe } from '../../../gallery-shared/pipes/can-display-asset.pipe';
import { canDisplayAsset } from '../../../lib/can-display-asset';
import { devicePixelRatio } from '../../../lib/device-pixel-ratio';
import { getCopyright } from '../../../lib/extract-exif';
import { PICFLOW_CONFIG } from '../../../picflow-config';
import { AssetSupportsTransparencyPipe } from '../../../pipes/asset-supports-transparency.pipe';
import { CommentService } from '../../../services/comment.service';
import { StateService } from '../../../services/state.service';
import { ClickOutsideDirective } from '../../../shared-extra/directives/click-outside.directive';
import { LoadingIndicatorComponent } from '../../../shared/components/loading-indicator/loading-indicator.component';
import { AssetVersionUrlPipe } from '../../../shared/pipes/asset-version-url.pipe';
import { getAssetVersionDimensions } from '../../lib/get-asset-version-dimensions';
import { NonPreviewFileComponent } from '../non-preview-file/non-preview-file.component';
import { PdfViewerComponent } from '../pdf-viewer/pdf-viewer.component';

@Component({
  selector: 'app-asset-page-item',
  imports: [
    AssetVersionUrlPipe,
    VideoViewerComponent,
    ClickOutsideDirective,
    AssetSupportsTransparencyPipe,
    NonPreviewFileComponent,
    PdfViewerComponent,
    AudioViewerComponent,
    LoadingIndicatorComponent,
    CanDisplayAssetPipe,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './asset-page-item.component.html',
  styleUrl: './asset-page-item.component.scss',
})
export class AssetPageItemComponent {
  public state = inject(StateService);
  public commentService = inject(CommentService);
  public route = inject(ActivatedRoute);
  public config = inject(PICFLOW_CONFIG);

  readonly asset = input.required<AssetDto>();
  readonly offsetScale = input.required<number>();
  readonly offset = input<Rect>(new Rect());
  readonly scale = input.required<number>();
  readonly crop = input.required<Rect>();

  readonly videoViewer = viewChild<VideoViewerComponent>('videoViewer');

  readonly gallery = input.required<GalleryDto>();
  readonly theme = input.required<GalleryTheme>();
  readonly isActive = input.required<boolean>();
  readonly dragging = input<boolean>();
  readonly sideMenuOpen = input<boolean>();
  readonly controlsVisible = input<boolean>();
  readonly activePage = input<number>();
  readonly commentPlacementMode = input<'drag' | 'place'>();
  readonly verticalDragDelta = input<number>();
  readonly videoFocused = input<boolean>();
  readonly galleryPermissions = input<GalleryDto['permissions']>();
  readonly isTeamMember = input<boolean>();
  readonly isDesktop = input<boolean>();
  readonly annotationMode = input<boolean>();
  readonly selectedThreadId = input<string>();
  readonly showResolved = input<boolean>();
  readonly animateOffset = input<boolean>();
  readonly blockVideoClick = input<boolean>();

  readonly activePageChange = output<number>();
  readonly onOffsetChange = output<number>();
  readonly videoFocusedChange = output<boolean>();
  readonly onTimeChange = output<VideoTimeData>();
  readonly selectThreadFromFace = output<CommentThread>();

  readonly copyrightInfoPos = signal<Rect>(undefined);

  private readonly tenant = computed(() => {
    const gallery = this.gallery();
    if (!gallery) return;
    if (typeof gallery.tenant === 'string') {
      return [...(this.state.tenants || []), ...(this.state.sharedWithMeTenants || [])].find(
        t => t.id === gallery.tenant
      );
    }
    return gallery.tenant;
  });
  readonly copyrightMessage = computed(() => {
    const tenant = this.tenant();
    const asset = this.asset();
    if (!tenant || !asset) return;

    const exif = (asset.version_data?.data as AssetImageTypeDataDto)?.exif;
    const exifCopyright = exif && getCopyright(exif);
    if (exifCopyright) return exifCopyright;
    if (tenant.name) return tenant.name;
    return 'Copyright ' + new Date().getFullYear();
  });

  showLoadingIndicator = computed(() => {
    const asset = this.asset();
    const imageLoaded = this.imageLoaded();
    if (!asset) return;
    if (asset.type !== 'image' && asset.type !== 'document') return false;
    if (!canDisplayAsset(asset)) return false;
    return !imageLoaded;
  });

  private readonly assetId = computed(() => this.asset()?.id);
  readonly imageLoaded = linkedSignal({
    source: this.assetId,
    computation: () => false,
  });

  load() {
    this.imageLoaded.set(true);
  }

  readonly autoplayOnLoad = toSignal(this.route.queryParams.pipe(map(params => !!params.autoplay)));

  readonly displayWidth = computed(() => {
    const _offsetScale = this.offsetScale();
    const asset = this.asset();
    if (!_offsetScale || !asset) return;
    const offsetScale = Math.min(1, _offsetScale);
    const { width } = getAssetVersionDimensions(asset.version_data, this.getDataBackup());
    return width * offsetScale;
  });

  readonly displayHeight = computed(() => {
    const _offsetScale = this.offsetScale();
    const asset = this.asset();
    if (!_offsetScale || !asset) return;
    const offsetScale = Math.min(1, _offsetScale);
    const { height } = getAssetVersionDimensions(asset.version_data, this.getDataBackup());
    return height * offsetScale;
  });

  readonly pdfBaseSrc = computed(() => {
    const asset = this.asset();
    if (!asset || asset.type !== 'document') return;
    return (
      this.config.assetsEndpoint + '/assets/' + asset.version_data.original_key.replace('-original.pdf', '-base.pdf')
    );
  });

  private getDataBackup() {
    return { width: 250 * devicePixelRatio(), height: 250 * devicePixelRatio() };
  }

  private allowDownload() {
    if (this.galleryPermissions().can_edit) return true;
    if (!this.gallery().download_single_asset) return false;
    if (this.gallery().approved_downloads_only) {
      return this.asset()?.status === 'approved';
    }
    return true;
  }

  onContextMenu(event: MouseEvent) {
    if (this.isDesktop() && this.allowDownload()) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    if (!this.allowDownload()) {
      this.copyrightInfoPos.set(new Rect(event.clientX, event.clientY));
    }
  }
}
